import React from 'react';
import { Box, Typography } from '@mui/material';
import CommonStatusPill from '../../common/StatusPill';

const TONE_TO_VARIANT = {
  success: 'green',
  warning: 'amber',
  error: 'red',
  waiting: 'working',
  neutral: 'neutral',
};

/** Theme-token palette — mode-aware via CSS vars in index.jsx. */
export const monotonePalette = {
  surface: 'var(--color-bg-paper)',
  surfaceSubtle: 'var(--color-bg-subtle)',
  border: 'var(--color-border)',
  textPrimary: 'var(--color-text-primary)',
  textSecondary: 'var(--color-text-secondary)',
  successBg: 'var(--status-green-bg)',
  successText: 'var(--status-green-color)',
  warningBg: 'var(--status-amber-bg)',
  warningText: 'var(--status-amber-color)',
  errorBg: 'var(--status-red-bg)',
  errorText: 'var(--status-red-color)',
  focusRing: 'color-mix(in srgb, var(--color-text-primary) 8%, transparent)',
  focusRingError: 'color-mix(in srgb, var(--status-red-color) 12%, transparent)',
  accent: 'var(--color-text-primary)',
  accentHover: 'var(--color-text-secondary)',
  inputBorder: 'var(--color-border)',
  inputBorderHover: 'var(--color-text-secondary)',
  inputBorderFocus: 'var(--color-text-primary)',
  inputBg: 'transparent',
  shadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)',
  fieldHoverBg: 'var(--color-hover)',
  chipBg: 'var(--color-bg-subtle)',
  chipText: 'var(--color-text-secondary)',
  chipBorder: 'var(--color-border)',
  selectedBorder: 'var(--color-text-primary)',
  selectedBg: 'var(--color-hover)',
  pageBg: 'var(--color-bg-subtle)',
};

export function useFormPalette() {
  return monotonePalette;
}

const STATUS_LABELS = {
  NOT_STARTED: 'Not started',
  ACTIVE: 'Active',
  DONE: 'Done',
  CANCELLED: 'Cancelled',
};

export function formatStatusLabel(value) {
  if (value === null || value === undefined) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (STATUS_LABELS[raw]) return STATUS_LABELS[raw];

  return raw
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatProgressTone(progress) {
  if (progress === 'DONE') return 'success';
  if (progress === 'CANCELLED') return 'error';
  if (progress === 'ACTIVE') return 'warning';
  return 'neutral';
}

export function FormSurface({ title, subtitle, rightSlot, children }) {
  const palette = useFormPalette();
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: "10px",
        overflow: "hidden",
        backgroundColor: palette.surface,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderBottom: `1px solid ${palette.border}`,
          backgroundColor: palette.surfaceSubtle,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          rowGap: 0.75,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: palette.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: "11px", color: palette.textSecondary, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {rightSlot}
      </Box>
      <Box sx={{ p: 1.5, minWidth: 0, overflowX: "auto" }}>{children}</Box>
    </Box>
  );
}

export function StatusPill({ label, tone = 'neutral', formatLabel = true }) {
  const displayLabel = formatLabel ? formatStatusLabel(label) : label;
  const variant = TONE_TO_VARIANT[tone] || 'neutral';

  return (
    <CommonStatusPill variant={variant} size="sm">
      {displayLabel}
    </CommonStatusPill>
  );
}

/** Active forms/tasks show a purple "Waiting" pill aligned with session status colors. */
export function FormProgressChip({ progressStatus, isActive }) {
  if (isActive) {
    return <StatusPill label="Waiting" tone="waiting" formatLabel={false} />;
  }
  if (!progressStatus) return null;
  return <StatusPill label={progressStatus} tone={formatProgressTone(progressStatus)} />;
}
